<template>
  <div
    :class="[$style.config, $style.product, isActive && $style.activeProduct]"
    data-product-selector-item-v="3.1.0"
  >
    <div :class="$style.imageWrapper">
      <BasePicture
        v-if="product?.fields?.images?.[0]?.fields?.file?.url"
        :class="$style.picture"
        :img-class="$style.image"
        alt=""
        :src="`${product?.fields?.images?.[0]?.fields?.file?.url}?w=160&h=160&fit=pad`"
      />
    </div>
    <div v-if="computedVariants.length > 1" :class="$style.count">
      {{ computedVariants.length }}
    </div>
    <div :class="$style.name">
      {{ product?.fields?.type?.fields?.label || product?.fields?.name || '' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())

const props = defineProps<{
  product: TypeProductWithoutUnresolvableLinksResponse
  isActive: boolean
}>()

const computedVariants = computed(() => {
  return (
    props.product?.fields?.variants?.filter((p) => {
      const isVisible = $visibility(p?.fields?.visibility)
      const hasAccess = $userAccessCheck(
        p?.fields?.accessLevel,
        highestUserLevel.value,
      )
      return isVisible && hasAccess
    }) || []
  )
})
</script>

<style lang="scss" module>
.config {
  --image-size: 100px;

  @include media('md') {
    --image-size: 160px;
  }
}

.product {
  background-color: var(--white);
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 13px 0 rgba(0, 0, 0, 0.06);
  cursor: pointer;
  height: 100%;
  padding: 12px;
}

.activeProduct {
  box-shadow: 0 7px 17px 0 rgba(0, 0, 0, 0.22);
  transform: translateY(-15px);
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
  }

  &:before {
    background-color: var(--purple);
    bottom: 0;
    left: 0;
    height: 5px;
    transform: translateY(100%);
    width: 100%;
  }

  &:after {
    bottom: -5px;
    border-bottom-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-color: var(--purple);
    border-style: solid;
    border-width: 10px;
    left: 50%;
    height: 0;
    transform: translate(-50%, 100%);
    width: 0;
  }
}

.imageWrapper {
  height: var(--image-size);
  margin: -40px auto 10px;
  width: var(--image-size);
}

.picture {
  height: 100%;
}

.image {
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.count {
  margin: 0 auto;
  margin-bottom: 4px;
  padding: 8px;
  min-width: 24px;
  width: min-content;
  height: 24px;
  font-weight: 600;
  font-size: to-rem(15px);
  line-height: 8px;
  background: var(--purple);
  border-radius: 9999px;
  color: var(--white);
  text-align: center;
}

.name {
  font-family: var(--roboto-condensed);
  font-size: to-rem(20px);
  font-weight: bold;
  line-height: math.div(24, 20);
  text-align: center;
  min-height: 2.5em;
}
</style>
